import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface SubscriptionData {
  id: string;
  product_name: string;
  billing_cycle: 'monthly' | 'quarterly' | 'yearly';
  fee: number;
}

interface AppSettings {
  bkash_send_money_number: string;
  subscription_monthly_fee: number;
}

export default function PaymentNow() {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [payerNumber, setPayerNumber] = useState('');
  const [trxId, setTrxId] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
    generateReferenceCode();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const { data: subData, error: subError } = await supabase
        .from('software_subscription_settings')
        .select('*')
        .order('updated_at', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (subError) {
        console.error('Subscription query error:', subError);
        alert(`Failed to load subscription: ${subError.message || 'Unknown error'}`);
        return;
      }
      setSubscription(subData);
      if (subData) {
        setBillingCycle(subData.billing_cycle);
      }

      const { data: settingsData, error: settingsError } = await supabase
        .from('app_settings')
        .select('*')
        .in('key', ['bkash_send_money_number', 'subscription_monthly_fee']);

      if (settingsError) {
        console.error('Settings query error:', settingsError);
        alert(`Failed to load app settings: ${settingsError.message || 'Unknown error'}`);
        return;
      }

      const raw: any = {};
      settingsData?.forEach(({ key, value }) => {
        raw[key] = value;
      });

      const normalized = {
        bkash_send_money_number: String(raw.bkash_send_money_number ?? '01681354066'),
        subscription_monthly_fee: Number(raw.subscription_monthly_fee ?? 1000),
      };

      setSettings(normalized);
    } catch (error: any) {
      console.error('Failed to load data:', error);
      alert(`Failed to load payment data: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const generateReferenceCode = async () => {
    try {
      const { data, error } = await supabase.rpc('generate_reference_code');
      if (error) throw error;
      setReferenceCode(data);
    } catch (error) {
      console.error('Failed to generate reference code:', error);
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substring(2, 8).toUpperCase();
      setReferenceCode(`SB-${timestamp}-${random}`);
    }
  };

  const calculateAmount = () => {
    if (!subscription) return 0;
    
    const baseFee = subscription.fee;
    switch (billingCycle) {
      case 'monthly':
        return baseFee;
      case 'quarterly':
        return baseFee * 3;
      case 'yearly':
        return baseFee * 12;
      default:
        return baseFee;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!payerNumber) {
      alert('Please enter your bKash number');
      return;
    }

    if (payerNumber.length !== 11) {
      alert('Please enter a valid 11-digit bKash number');
      return;
    }

    try {
      setSubmitting(true);

      const amount = calculateAmount();
      const bkashNumber = settings?.bkash_send_money_number || '01681354066';

      const { error } = await supabase
        .from('software_payments')
        .insert({
          type: 'subscription',
          amount: amount,
          charge_amount: 0,
          total_amount: amount,
          payment_method: 'bkash',
          channel: 'send_money',
          bkash_send_number: bkashNumber,
          payer_number: payerNumber,
          reference_code: referenceCode,
          trx_id: trxId || null,
          billing_cycle: billingCycle,
          status: 'pending',
        });

      if (error) throw error;

      alert('Payment request submitted successfully! Your payment is pending approval.');
      navigate('/softwarebilling/subscription');
    } catch (error) {
      console.error('Failed to submit payment:', error);
      alert('Failed to submit payment request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading payment details...</div>
      </div>
    );
  }

  if (!subscription || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Failed to load payment details</div>
      </div>
    );
  }

  const amount = calculateAmount();
  const bkashNumber = settings.bkash_send_money_number || '01681354066';

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payment Details</h1>
        <p className="text-gray-600 mt-1">Complete your subscription payment via bKash</p>
      </div>

      {/* bKash Payment Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-center mb-6">
          <img 
            src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 60'%3E%3Ctext x='10' y='40' font-family='Arial' font-size='32' font-weight='bold' fill='%23E2136E'%3EbKash%3C/text%3E%3Ctext x='120' y='40' font-family='Arial' font-size='20' fill='%23E2136E'%3EPayment%3C/text%3E%3C/svg%3E" 
            alt="bKash Payment" 
            className="h-16"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Name
            </label>
            <div className="text-lg font-semibold text-gray-900">
              {subscription.product_name}
            </div>
          </div>

          {/* Billing Cycle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billing Cycle <span className="text-red-500">*</span>
            </label>
            <select
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">--Select--</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly (3 months)</option>
              <option value="yearly">Yearly (12 months)</option>
            </select>
          </div>

          {/* Amount Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Total Amount</div>
            <div className="text-3xl font-bold text-primary-600">
              {formatCurrency(amount)}
            </div>
          </div>

          {/* bKash Instructions */}
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <div className="text-sm font-medium text-pink-900 mb-2">
              bKash Send Money: {bkashNumber}
            </div>
            <div className="text-sm text-pink-800">
              Reference: {referenceCode}
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Payment Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open your bKash app</li>
                  <li>Select "Send Money"</li>
                  <li>Enter the number: {bkashNumber}</li>
                  <li>Enter amount: {formatCurrency(amount)}</li>
                  <li>Complete the payment</li>
                  <li>Enter your bKash number and transaction ID below</li>
                </ol>
              </div>
            </div>
          </div>

          {/* bKash Number Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              bKash Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={payerNumber}
              onChange={(e) => setPayerNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="01XXXXXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              maxLength={11}
            />
            <p className="text-xs text-gray-500 mt-1">Enter the bKash number you used for payment</p>
          </div>

          {/* Transaction ID Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction ID (Optional)
            </label>
            <input
              type="text"
              value={trxId}
              onChange={(e) => setTrxId(e.target.value)}
              placeholder="Enter bKash transaction ID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-pink-600 text-white py-3 px-6 rounded-lg hover:bg-pink-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center"
          >
            <CreditCard className="h-5 w-5 mr-2" />
            {submitting ? 'Submitting...' : 'Pay bKash'}
          </button>
        </form>
      </div>
    </div>
  );
}
